import React, { useEffect, useState } from "react";
import DashboardNav from "@/components/DashboardNav";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Copy, Check, ExternalLink, Lock } from "lucide-react";

const FIELDS = [
  { key: "clinic_name", label: "Clinic Name" },
  { key: "clinic_tagline", label: "Tagline (shown to patients)" },
  { key: "treatment_name", label: "Treatment name (used on your page & in all emails)" },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Address" },
  { key: "booking_url", label: "Booking URL (Calendly / clinic site)" },
];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await api.post("/clinics/logo", fd);
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      await refreshUser();
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setForm({
        clinic_name: user.clinic_name || "",
        clinic_tagline: user.clinic_tagline || "",
        treatment_name: user.treatment_name || "clear aligners",
        phone: user.phone || "",
        address: user.address || "",
        booking_url: user.booking_url || "",
        logo_url: user.logo_url || "",
        primary_color: user.primary_color || "#1C352D",
      });
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/clinics/me", form);
      await refreshUser();
      toast.success("Clinic settings saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-bone-100">
      <DashboardNav />
      <main className="max-w-3xl mx-auto p-6 md:p-10">
        <div className="overline mb-2">Clinic Settings</div>
        <h1 className="font-heading text-4xl text-forest-700">Your branded patient page</h1>
        <p className="text-sm text-forest-400 mt-2 max-w-xl">
          These details appear on your public smile preview link. Patients see
          your clinic name, tagline and booking link after their result.
        </p>

        <form onSubmit={save} className="mt-10 space-y-6 bg-white border border-bone-200 p-6 md:p-10" data-testid="settings-form">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="label-flat">{f.label}</label>
              <input
                className="input-flat"
                value={form[f.key] || ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                data-testid={`settings-${f.key}`}
              />
            </div>
          ))}

          <div>
            <label className="label-flat">Clinic logo</label>
            <div className="flex items-center gap-4">
              {form.logo_url ? (
                <img src={form.logo_url} alt="Clinic logo" className="w-16 h-16 object-contain border border-bone-200 bg-white" data-testid="settings-logo-preview" />
              ) : (
                <div className="w-16 h-16 border border-dashed border-bone-300 flex items-center justify-center text-[10px] text-forest-400 text-center">No logo</div>
              )}
              <div>
                <label className="btn-secondary !py-2 !px-4 text-sm cursor-pointer inline-flex" data-testid="settings-logo-upload-label">
                  {uploading ? "Uploading…" : "Upload logo"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => uploadLogo(e.target.files?.[0])}
                    data-testid="settings-logo-file"
                  />
                </label>
                <p className="text-xs text-forest-400 mt-1">PNG, JPG, SVG or WebP, up to 5MB.</p>
              </div>
            </div>
            <div className="mt-3">
              <label className="label-flat">Or paste a logo URL</label>
              <input
                className="input-flat"
                value={form.logo_url || ""}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://…"
                data-testid="settings-logo_url"
              />
            </div>
          </div>

          <div>
            <label className="label-flat">Primary brand color</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.primary_color || "#1C352D"}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className="w-14 h-12 border border-bone-200 cursor-pointer"
                data-testid="settings-color"
              />
              <input
                className="input-flat font-mono text-sm"
                value={form.primary_color || ""}
                onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
              />
            </div>
          </div>

          <div className="pt-4">
            <button type="submit" disabled={saving} className="btn-primary" data-testid="settings-save">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>

          <div className="pt-6 border-t border-bone-200">
            <div className="overline mb-2">Your public link</div>
            <div className="font-mono text-sm text-forest-700 bg-bone-100 border border-bone-200 px-3 py-2">
              {window.location.origin}/preview/{user.clinic_slug}
            </div>
          </div>
        </form>

        <EmbedSection user={user} />
      </main>
    </div>
  );
}

function EmbedSection({ user }) {
  const origin = window.location.origin;
  const slug = user.clinic_slug;
  const isPaid = user.plan && user.plan !== "trial";
  const snippet = `<div data-toothpix-clinic="${slug}"></div>\n<script src="${origin}/embed.js" async></script>`;
  const iframeSnippet = `<iframe src="${origin}/embed/${slug}" style="width:100%;border:0;min-height:680px;" allow="fullscreen"></iframe>`;
  const [tab, setTab] = useState("script");
  const [copied, setCopied] = useState(false);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <section className="mt-12 bg-white border border-bone-200" data-testid="embed-section">
      <div className="px-6 md:px-10 py-6 border-b border-bone-200 flex items-start justify-between gap-4">
        <div>
          <div className="overline">Embed on your website</div>
          <h2 className="font-heading text-2xl text-forest-700 mt-2">Drop the smile preview anywhere.</h2>
          <p className="text-sm text-forest-400 mt-2 max-w-lg">
            Paste this two-line snippet on your clinic site, your landing page, or your "Book a consult" tab.
            Every patient who tries it becomes a lead in this dashboard.
          </p>
        </div>
        {!isPaid && (
          <div className="bg-forest-600 text-white px-3 py-2 text-[10px] uppercase tracking-[0.2em] flex items-center gap-1.5 shrink-0">
            <Lock size={11} /> Paid plans
          </div>
        )}
      </div>

      <div className="px-6 md:px-10 py-6">
        {!isPaid && (
          <div className="mb-6 border border-bone-300 bg-bone-200/50 p-4 text-sm text-forest-700" data-testid="embed-upgrade-notice">
            Embed is included on the Clinic plan (and during pilots). On the free trial you can preview the snippet, but it won't render on your site until you upgrade. Open the <a href="/dashboard" className="underline">dashboard</a> to upgrade.
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-bone-200">
          {[
            { id: "script", label: "Script tag (recommended)" },
            { id: "iframe", label: "Raw iframe" },
            { id: "wp", label: "WordPress" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`embed-tab-${t.id}`}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] -mb-px ${
                tab === t.id ? "border-b-2 border-forest-600 text-forest-600" : "text-forest-400 hover:text-forest-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Snippet body */}
        <div className="mt-5">
          {tab === "script" && (
            <SnippetBlock label="Paste before </body>" code={snippet} onCopy={copy} copied={copied} testid="embed-snippet-script" />
          )}
          {tab === "iframe" && (
            <SnippetBlock label="Drop anywhere in your HTML" code={iframeSnippet} onCopy={copy} copied={copied} testid="embed-snippet-iframe" />
          )}
          {tab === "wp" && (
            <div className="space-y-4">
              <p className="text-sm text-forest-400">
                In WordPress, add a <b>Custom HTML</b> block to any page, and paste:
              </p>
              <SnippetBlock label="WordPress Custom HTML block" code={snippet} onCopy={copy} copied={copied} testid="embed-snippet-wp" />
              <p className="text-xs text-forest-400">
                The widget will load asynchronously, won't block your page, and resizes itself to fit the upload form, the processing animation, and the result screen.
              </p>
            </div>
          )}
        </div>

        {/* Live preview */}
        <div className="mt-8">
          <div className="overline mb-2">Live preview</div>
          <div className="bg-bone-100 border border-bone-200 p-4">
            <div className="text-xs text-forest-400 mb-3">
              This is exactly what visitors of your website will see:
            </div>
            <div className="bg-white border border-bone-200" data-testid="embed-live-preview">
              <iframe
                title="Toothpix embed preview"
                src={`${origin}/embed/${slug}`}
                style={{ width: "100%", border: 0, minHeight: 680, display: "block" }}
              />
            </div>
            <div className="text-xs text-forest-400 mt-3">
              Open the live URL: <a href={`${origin}/embed/${slug}`} target="_blank" rel="noreferrer" className="text-forest-600 underline inline-flex items-center gap-1">{origin}/embed/{slug} <ExternalLink size={11} /></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SnippetBlock({ label, code, onCopy, copied, testid }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      <div className="relative">
        <pre
          className="bg-forest-700 text-bone-100 font-mono text-xs leading-relaxed p-4 overflow-x-auto whitespace-pre-wrap"
          data-testid={testid}
        >{code}</pre>
        <button
          type="button"
          onClick={() => onCopy(code)}
          className="absolute top-2 right-2 bg-white text-forest-600 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] hover:bg-bone-100 inline-flex items-center gap-1"
          data-testid={`${testid}-copy`}
        >
          {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
        </button>
      </div>
    </div>
  );
}
